import type { FC } from "react";

import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import type { VirtualPrizeBadgesProps } from "@jti/ui/components/VirtualPrizeBadges";
import { VirtualPrizeBadges } from "@jti/ui/components/VirtualPrizeBadges";

import { PointsProgressBar } from "../PointsProgressBar";

import styles from "./RewardCard.module.scss";

export type RewardCardProps = {
  title: string;
  description: string;
  discoverButtonLabel?: string;
  discoverButtonUrl?: string;
  discoverButtonOnClick?: () => void;
  backgroundImageUrl?: string;
  primaryCtaLabel?: string;
  primaryCtaUrl?: string;
  primaryCtaOnClick?: () => void;
  isPrimaryButtonDisabled?: boolean;
  isPrimaryButtonHidden?: boolean;
  color?: "white" | "black";
  size?: "default" | "small";
  variant?: "default" | "blurredBackground";
  pointsLabel: string;
  currentPoints?: number;
  requiredPoints?: number;
  isProgressBarHidden?: boolean;
  shouldShowVirtualBadges?: boolean;
} & VirtualPrizeBadgesProps;

export const RewardCard: FC<RewardCardProps> = ({
  backgroundImageUrl,
  title,
  description,
  discoverButtonLabel,
  discoverButtonUrl,
  discoverButtonOnClick,
  primaryCtaLabel,
  primaryCtaUrl,
  primaryCtaOnClick,
  isPrimaryButtonDisabled,
  isPrimaryButtonHidden,
  color,
  hasLimitedOfferBadge,
  newBadgeLabel,
  showNewBadge,
  isTierRestricted,
  tier,
  outOfStockLabel,
  isOutOfStock,
  timeLimitedBadgeLabel,
  isMoreThanOneDay,
  size,
  variant,
  pointsLabel,
  currentPoints,
  requiredPoints,
  isProgressBarHidden,
  shouldShowVirtualBadges,
}) => {
  const isDiscoveryButtonVisible = Boolean(discoverButtonLabel && discoverButtonUrl);
  const shouldRenderForegroundImage = Boolean(backgroundImageUrl && variant === "blurredBackground");

  return (
    <div
      className={classNames(styles.rewardCard, {
        [styles.containerSmall]: size === "small",
        [styles.containerBlurredBackground]: variant === "blurredBackground",
        "dark-theme": color === "white",
        "light-theme": color === "black",
      })}
      style={{
        "--reward-card-background-image": `url('${backgroundImageUrl}')`,
      }}
    >
      <div className={styles.topSection}>
        <div
          className={classNames(styles.badgesWrapper, {
            [styles.badgeAtTheEnd]: (!showNewBadge && !hasLimitedOfferBadge) || isTierRestricted || isOutOfStock,
          })}
        >
          {shouldShowVirtualBadges && (
            <VirtualPrizeBadges
              showNewBadge={showNewBadge}
              isTierRestricted={isTierRestricted}
              tier={tier}
              isOutOfStock={isOutOfStock}
              hasLimitedOfferBadge={hasLimitedOfferBadge}
              newBadgeLabel={newBadgeLabel}
              timeLimitedBadgeLabel={timeLimitedBadgeLabel}
              isMoreThanOneDay={isMoreThanOneDay}
              color={color}
              outOfStockLabel={outOfStockLabel}
              shouldResetPositioning
            />
          )}
        </div>
        <div className={styles.textWrapper}>
          <span className={styles.title}>{title}</span>
          <span className={styles.description}>{description}</span>
        </div>
      </div>
      {shouldRenderForegroundImage && (
        <div className={styles.imageSection}>
          <ImageBehaviour
            src={backgroundImageUrl}
            alt={title}
            className={styles.foregroundImage}
            width={400}
            height={300}
          />
        </div>
      )}
      <div className={styles.bottomSection}>
        <div className={styles.pointsWrapper}>
          <span className={styles.pointsLabel}>{pointsLabel}</span>
          <PointsProgressBar
            hidden={isProgressBarHidden}
            requiredPoints={requiredPoints ?? 0}
            currentPoints={currentPoints ?? 0}
          />
        </div>
        <div className={styles.buttonsWrapper}>
          {!isPrimaryButtonHidden && (
            <Button
              disabled={isPrimaryButtonDisabled}
              size="sm"
              variant="primary"
              href={primaryCtaUrl}
              onClick={primaryCtaOnClick}
            >
              {primaryCtaLabel ?? ""}
            </Button>
          )}
          {isDiscoveryButtonVisible && (
            <Button href={discoverButtonUrl} onClick={discoverButtonOnClick} variant="secondary" size="sm">
              {discoverButtonLabel ?? ""}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
